import styled, { css } from "styled-components/macro";

export const BaseButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border: none;
  border-radius: 24px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  padding: 10px;

  /* Success State */
  &[data-success="true"] {
    background: #f0f7f0;
    color: var(--Success-100);
    cursor: not-allowed;

    svg {
      color: var(--Success-100);
    }

    &:hover {
      background: #f0f7f0;
    }
  }

  /* Sizes */
  ${(props) =>
    props.size === "S" &&
    css`
      height: 32px;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.018px;

      svg {
        width: 14px;
        height: 14px;
      }
    `}

  ${(props) =>
    props.size === "M" &&
    css`
      height: 40px;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.021px;

      svg {
        width: 16px;
        height: 16px;
      }
    `}

  /* Variant + ColorScheme */
  ${({ variant, colorScheme }) => {
    const schemes = {
      neutral: {
        primary: css`
          background: var(--Neutral-100);
          color: var(--White-100);

          &:hover {
            background: var(--Neutral-80);
          }

          &:disabled {
            cursor: not-allowed;
            background: rgba(31, 31, 31, 0.4);
          }
        `,
        secondary: css`
          background: rgba(31, 31, 31, 0.04);
          color: var(--Neutral-100);

          &:hover {
            background: rgba(31, 31, 31, 0.08);
          }

          &:disabled {
            cursor: not-allowed;
            background: rgba(31, 31, 31, 0.04);
          }
        `,
        tertiary: css`
          background: transparent;
          color: var(--Neutral-100);

          &:hover {
            background: rgba(31, 31, 31, 0.02);
          }

          &:disabled {
            cursor: not-allowed;
            background: rgba(31, 31, 31, 0.02);
          }
        `
      },
      brand: {
        primary: css`
          background: var(--Brand-100);
          color: var(--White-100);

          &:hover {
            background:
              linear-gradient(
                0deg,
                rgba(160, 73, 252, 0.8) 0%,
                rgba(160, 73, 252, 0.8) 100%
              ),
              #fff;
          }

          &:disabled {
            cursor: not-allowed;
            background:
              linear-gradient(
                0deg,
                rgba(160, 73, 252, 0.4) 0%,
                rgba(160, 73, 252, 0.4) 100%
              ),
              #fff;
          }
        `,
        secondary: css`
          background:
            linear-gradient(
              0deg,
              rgba(160, 73, 252, 0.04) 0%,
              rgba(160, 73, 252, 0.04) 100%
            ),
            #fff;
          color: var(--Brand-100);

          &:hover {
            background:
              linear-gradient(
                0deg,
                rgba(160, 73, 252, 0.08) 0%,
                rgba(160, 73, 252, 0.08) 100%
              ),
              #fff;
          }

          &:disabled {
            cursor: not-allowed;
            background:
              linear-gradient(
                0deg,
                rgba(160, 73, 252, 0.04) 0%,
                rgba(160, 73, 252, 0.04) 100%
              ),
              #fff;
          }
        `,
        tertiary: css`
          background: transparent;
          color: var(--Brand-100);

          &:hover {
            background:
              linear-gradient(
                0deg,
                rgba(160, 73, 252, 0.02) 0%,
                rgba(160, 73, 252, 0.02) 100%
              ),
              #fff;
          }

          &:disabled {
            cursor: not-allowed;
            background:
              linear-gradient(
                0deg,
                rgba(160, 73, 252, 0.02) 0%,
                rgba(160, 73, 252, 0.02) 100%
              ),
              #fff;
          }
        `
      },
      error: {
        primary: css`
          background: var(--Error-100);
          color: var(--White-100);

          &:hover {
            background: var(--Error-80);
          }

          &:disabled {
            cursor: not-allowed;
            background: var(--Error-40);
          }
        `,
        secondary: css`
          background: var(--Error-40);
          color: var(--Error-100);

          &:hover {
            background: var(--Error-80);
          }

          &:disabled {
            cursor: not-allowed;
            background: var(--Error-40);
          }
        `,
        tertiary: css`
          background: transparent;
          color: var(--Error-100);

          &:hover {
            background: var(--Error-2);
          }

          &:disabled {
            cursor: not-allowed;
            background: var(--Error-2);
          }
        `
      },
      success: {
        primary: css`
          background: var(--Success-100);
          color: var(--White-100);

          &:hover {
            background: var(--Success-80);
          }

          &:disabled {
            cursor: not-allowed;
            background: var(--Success-40);
          }
        `,
        secondary: css`
          background: var(--Success-40);
          color: var(--Success-100);

          &:hover {
            background: var(--Success-80);
          }

          &:disabled {
            cursor: not-allowed;
            background: var(--Success-40);
          }
        `,
        tertiary: css`
          background: transparent;
          color: var(--Success-100);

          &:hover {
            background: var(--Success-2);
          }

          &:disabled {
            cursor: not-allowed;
            background: var(--Success-2);
          }
        `
      }
    };

    return schemes[colorScheme || "neutral"]?.[variant] || "";
  }}

  @media (max-width: 768px) {
    &.decision-pill {
      width: 100%;
    }
  }
`;

export const Group = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 2px;
  height: 32px;
  padding: 0 2px;
  border-radius: 32px;
  width: max-content;

  /* Border color based on colorScheme */
  border: 1px solid
    ${(props) =>
      props.colorScheme === "brand" ? "var(--Brand-100)" : "var(--Neutral-40)"};

  /* Sizes */
  ${(props) =>
    props.size === "S" &&
    css`
      height: 32px;
    `}

  ${(props) =>
    props.size === "M" &&
    css`
      height: 40px;
    `}
`;

export const StyledButton = styled.button`
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 6px;
  border-radius: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  font-size: 14px;
  height: 28px;

  /* Sizes */
  ${(props) =>
    props.size === "S" &&
    css`
      height: 28px;
      font-size: 12px;
      line-height: 16px;
      letter-spacing: -0.018px;
    `}

  ${(props) =>
    props.size === "M" &&
    css`
      height: 36px;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: -0.021px;
    `}

  /* Active / Inactive Styles based on colorScheme */
  ${({ active, colorScheme }) => {
    const colors = {
      brand: {
        activeBg: "var(--Brand-100)",
        activeColor: "#fff",
        hoverBg: "var(--Brand-20)",
        inactiveColor: "var(--Brand-100)"
      },
      neutral: {
        activeBg: "var(--Neutral-100)",
        activeColor: "#fff",
        hoverBg: "var(--Neutral-20)",
        inactiveColor: "var(--Neutral-100)"
      }
    };

    const scheme = colors[colorScheme] || colors.neutral;

    return active
      ? css`
          background: ${scheme.activeBg};
          color: ${scheme.activeColor};
        `
      : css`
          background: transparent;
          color: ${scheme.inactiveColor};

          &:hover {
            background: ${scheme.hoverBg};
          }
        `;
  }}
`;

export const PillButtonStyles = styled.button`
  border-radius: 24px;
  border: 1px solid var(--Neutral-8);
  background: transparent;
  display: inline-flex;
  padding: 8px 8px 8px 12px;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  transition: all 0.4s ease-in-out;
  gap: 4px;
  width: max-content;
  height: 32px;

  &.is-disabled {
    cursor: not-allowed;
    pointer-events: none;
  }

  &.pill-approved {
    background:
      linear-gradient(
        0deg,
        rgba(63, 145, 66, 0.08) 0%,
        rgba(63, 145, 66, 0.08) 100%
      ),
      rgb(255, 255, 255);
    border: 1px solid rgb(63, 145, 66);

    .placeholder {
      color: rgb(63, 145, 66);

      &.statusActive {
        color: rgb(63, 145, 66);
      }
    }

    .main-label {
      color: rgb(63, 145, 66);
    }

    &:hover {
      background:
        linear-gradient(
          0deg,
          rgba(63, 145, 66, 0.08) 0%,
          rgba(63, 145, 66, 0.08) 100%
        ),
        rgb(255, 255, 255);
      border: 1px solid rgb(63, 145, 66);
    }
  }

  &.pill-rejected {
    background:
      linear-gradient(
        0deg,
        rgba(198, 40, 40, 0.08) 0%,
        rgba(198, 40, 40, 0.08) 100%
      ),
      rgb(255, 255, 255);
    border: 1px solid rgb(198, 40, 40);

    .placeholder {
      color: rgb(198, 40, 40);
      &.statusActive {
        color: rgb(198, 40, 40);
      }
    }

    .main-label {
      color: rgb(198, 40, 40);
    }

    &:hover {
      background:
        linear-gradient(
          0deg,
          rgba(198, 40, 40, 0.08) 0%,
          rgba(198, 40, 40, 0.08) 100%
        ),
        rgb(255, 255, 255);
      border: 1px solid rgb(198, 40, 40);
    }
  }

  // background color based on status
  &.show-background {
    border: 1px solid ${(props) => props.color || "var(--Neutral-8)"};
    background: ${(props) =>
      props.color
        ? `linear-gradient(0deg, ${props.color}14 0%, ${props.color}14 100%), #fff`
        : "transparent"};

    &:hover {
      background: ${(props) =>
        props.color
          ? `linear-gradient(0deg, ${props.color}1A 0%, ${props.color}1A 100%), #fff`
          : "transparent"};
      border: 1px solid ${(props) => props.color || "var(--Neutral-8)"};
    }

    .main-label {
      color: ${(props) => props.color || "transparent"};
    }
  }

  &.active {
    border-radius: 24px;
    border: 1px solid var(--Neutral-100);
  }

  .left-icon {
    padding: 2px;
    display: flex;
    align-items: center;

    img {
      width: 12px;
      height: 12px;
    }
  }

  .placeholder {
    color: var(--Neutral-40);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: -0.012px;

    &.statusActive {
      color: var(--Neutral-100);
    }

    &.subscription-dropdown-placeholder {
      color: var(--Neutral-100);
    }

    &.sample-placeholder {
      color: var(--Neutral-40);
    }
  }

  .main-label {
    color: var(--Neutral-100);
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    letter-spacing: -0.018px;
    text-transform: capitalize;
  }

  .right-icon {
    padding: 2px;
    display: flex;
    align-items: center;

    svg {
      width: 12px;
      height: 12px;
    }
  }

  &:hover {
    border: 1px solid var(--Neutral-20);
    background: var(
      --Neutral-2,
      linear-gradient(
        0deg,
        rgba(31, 31, 31, 0.02) 0%,
        rgba(31, 31, 31, 0.02) 100%
      ),
      #fff
    );
  }
`;

export const StyledDropdownMenu = styled.div`
  position: absolute;
  top: 110%;
  z-index: 9;
  background: #fff;
  border: 1px solid #ddd;
  border-radius: 8px;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  padding: 4px;
  max-height: 240px;
  overflow-y: auto;

  &::-webkit-scrollbar {
    width: 0px;
    height: 0px;
  }

  &.align-left {
    left: 0;
    right: auto;
  }

  &.align-right {
    left: auto;
    right: 0;
  }

  &.filter-dropdown {
    min-width: max-content;
  }

  .dropdown-search {
    width: 100%;
    padding: 8px;
    border: none;
    border-bottom: 1px solid #eee;
    outline: none;
    color: var(--Neutral-100);
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    letter-spacing: -0.012px;
    text-transform: capitalize;
  }

  &.company-dropdown {
    max-width: 290px !important;
    min-width: 290px !important;
  }

  &.state-dropdown {
    max-width: 160px;
    min-width: 160px;

    &::-webkit-scrollbar {
      height: 8px;
      width: 8px;
    }

    &::-webkit-scrollbar-track {
      background-color: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background-color: var(--Neutral-300);
      border: 1px solid transparent;
      background-clip: content-box;
    }
  }

  li {
    list-style-type: none;
    padding: 8px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 4px;
    cursor: pointer;
    border-radius: 4px;

    &.selected {
      background: var(--Neutral-4);
    }

    &:hover {
      background: var(--Neutral-4);
    }

    .dropdown-content {
      display: flex;
      align-items: center;
      gap: 5px;

      .dropdown-left-icon {
        padding: 1px;

        svg {
          width: 14px;
          height: 14px;
        }
      }

      .dropdown-label {
        color: var(--Neutral-100);
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: -0.012px;
        text-transform: capitalize;
      }

      .count {
        color: var(--Neutral-100);
        font-size: 12px;
        font-weight: 400;
        line-height: 16px;
        letter-spacing: -0.012px;
        opacity: 0.4;
      }

      .dropdown-right-icon {
        padding: 1px;

        svg {
          width: 14px;
          height: 14px;
        }
      }
    }
  }

  ${({ size }) =>
    size === "S" &&
    css`
      min-width: fit-content;
    `}

  ${({ size }) =>
    size === "M" &&
    css`
      min-width: fit-content;
    `}
`;
